// frontend/src/components/RecordingPanel/RecordingPanel.js
//
// Dikteringspanel i journalvyn — ett TUNT skal mot det globala
// RecordingContext (inspelningen överlever navigering; se
// recording/RecordingContext.js). Visas bara för kliniker med
// has_notes_integration, endast i utkastläge.
//
// Ljudet går DIREKT från webbläsaren till Notes API med utbytt token;
// Journal-backenden ser aldrig ljudet, Notes ser aldrig patienten.

import React, { useState, useEffect, useRef } from 'react';
import { useRecordingSession } from '../../recording/RecordingContext';
import { getNotesStatus } from '../../notesClient';
import { getJournalTemplates } from '../PatientList/journalApi';
import './RecordingPanel.css';

const formatTime = (secs) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const RecordingPanel = ({ journal, patientName, autoStart = false, onAutoStartConsumed, onContentGenerated }) => {
  const rec = useRecordingSession();
  const [enabled, setEnabled] = useState(false);
  const [reachable, setReachable] = useState(false);
  const [statusChecked, setStatusChecked] = useState(false);
  const [templates, setTemplates] = useState([]);
  const autoStartTriedRef = useRef(false);

  const entryId = journal.entry_id;
  const isMine = rec && rec.entryId === entryId && rec.phase !== 'idle';
  const otherActive = rec && rec.entryId !== null && rec.entryId !== entryId &&
    ['recording', 'processing', 'generating'].includes(rec.phase);

  // Integrationsstatus + mickar + mallista vid mount
  useEffect(() => {
    let cancelled = false;
    getNotesStatus()
      .then((status) => {
        if (cancelled) return;
        setEnabled(Boolean(status.enabled));
        setReachable(Boolean(status.reachable));
        setStatusChecked(true);
        if (status.enabled) {
          rec.loadMicrophones();
          getJournalTemplates()
            .then((data) => { if (!cancelled) setTemplates(data.templates || []); })
            .catch(() => {});
        }
      })
      .catch(() => { if (!cancelled) { setStatusChecked(true); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bind till kontextet: genererat innehåll landar direkt i editorn
  useEffect(() => {
    if (!onContentGenerated) return undefined;
    return rec.bind(entryId, onContentGenerated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryId, onContentGenerated]);

  const handleStart = () => {
    rec.start({
      entryId,
      patientId: journal.patient_id,
      patientName: patientName || journal.patient?.fullname || '',
      templateRef: journal.template_ref,
      templateName: journal.template_name,
      sectionTitles: (journal.content?.sections || []).map((s) => s.title),
    });
  };

  // Autostart från "Starta inspelning" i Ny journal-modalen
  useEffect(() => {
    if (autoStart && statusChecked && enabled && reachable &&
        !autoStartTriedRef.current && !isMine && !otherActive) {
      autoStartTriedRef.current = true;
      if (onAutoStartConsumed) onAutoStartConsumed();
      handleStart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, statusChecked, enabled, reachable]);

  const handleTemplateSwitch = (ref) => {
    const t = templates.find((x) => x.template_ref === ref);
    if (t) {
      rec.setTemplate({
        ref: t.template_ref,
        name: t.template_name,
        sectionTitles: (t.sections || []).map((s) => s.title || s.section_title),
      });
    }
  };

  if (!statusChecked || !enabled) return null;

  return (
    <div className="recording-panel">
      {/* Notes onåbart — manuell skrivning opåverkad */}
      {!reachable && !isMine && (
        <div className="recording-panel-banner degraded">
          <i className="fas fa-microphone-slash"></i>
          Diktering otillgänglig — Aion Notes nås inte just nu. Du kan skriva journalen manuellt som vanligt.
        </div>
      )}

      {/* En annan journal spelas in */}
      {otherActive && (
        <div className="recording-panel-banner">
          <i className="fas fa-circle recording-elsewhere-dot"></i>
          En diktering pågår för {rec.patientName || 'en annan patient'} — stoppa den innan du startar en ny.
        </div>
      )}

      {/* Redo att starta */}
      {reachable && !isMine && !otherActive && (
        <div className="recording-panel-ready">
          <button className="btn btn-primary btn-sm recording-start-btn" onClick={handleStart}>
            <i className="fas fa-microphone"></i> Starta diktering
            {journal.template_name ? ` (${journal.template_name})` : ''}
          </button>
          {rec.microphones.length > 0 && (
            <select
              className="recording-mic-select"
              value={rec.selectedMicId}
              onChange={(e) => rec.setSelectedMicId(e.target.value)}
              title="Välj mikrofon"
            >
              {rec.microphones.map((m) => (
                <option key={m.deviceId} value={m.deviceId}>{m.label || 'Mikrofon'}</option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Pågående inspelning för DEN HÄR journalen */}
      {isMine && rec.phase === 'recording' && (
        <>
          <div className="recording-panel-active">
            <span className="recording-indicator">
              <span className="recording-dot"></span> Spelar in — {formatTime(rec.currentTime)}
            </span>
            <button className="btn btn-danger btn-sm" onClick={rec.stopAndGenerate}>
              <i className="fas fa-stop"></i> Stoppa och generera
            </button>
            {templates.length > 0 && (
              <select
                className="recording-mic-select"
                value={rec.template?.ref || ''}
                onChange={(e) => handleTemplateSwitch(e.target.value)}
                title="Byt journalmall — används vid genereringen"
              >
                {templates.map((t) => (
                  <option key={t.template_ref} value={t.template_ref}>{t.template_name}</option>
                ))}
              </select>
            )}
            {rec.uploadFailures > 0 && (
              <span className="recording-upload-warning" title="Ljudsnuttarna görs om automatiskt av Aion Notes">
                <i className="fas fa-exclamation-triangle"></i> {rec.uploadFailures} ljudsnutt{rec.uploadFailures === 1 ? '' : 'ar'} väntar på transkribering
              </span>
            )}
          </div>
          <div className="recording-panel-hint">
            Du kan navigera fritt i appen under inspelningen — den fortsätter i bakgrunden
            (se raden nere till höger).
          </div>
        </>
      )}

      {isMine && (rec.phase === 'processing' || rec.phase === 'generating') && (
        <div className="recording-panel-banner">
          <i className="fas fa-spinner fa-spin"></i>
          {rec.phase === 'processing' ? ' Bearbetar ljudet…' : ' Genererar journaltext…'}
        </div>
      )}

      {isMine && rec.phase === 'done' && (
        <div className="recording-panel-banner success">
          <i className="fas fa-check-circle"></i> Journaltext genererad och sparad i utkastet — granska innan du signerar.
        </div>
      )}

      {isMine && rec.phase === 'error' && (
        <div className="recording-panel-banner degraded">
          <i className="fas fa-exclamation-triangle"></i> {rec.errorMsg}
          {!rec.transcript && (
            <button className="btn btn-link btn-sm" onClick={rec.dismiss}>Försök igen</button>
          )}
        </div>
      )}

      {/* Efterläge: byt mall / generera om / diktera igen */}
      {isMine && (rec.phase === 'done' || (rec.phase === 'error' && rec.transcript)) && (
        <div className="recording-panel-after">
          {templates.length > 0 && (
            <select
              className="recording-mic-select"
              value={rec.template?.ref || ''}
              onChange={(e) => handleTemplateSwitch(e.target.value)}
              title="Byt journalmall — tryck sedan Generera om"
            >
              {templates.map((t) => (
                <option key={t.template_ref} value={t.template_ref}>{t.template_name}</option>
              ))}
            </select>
          )}
          <button className="btn btn-secondary btn-sm" onClick={rec.regenerate}
                  title="Kör om genereringen på samma transkript — ersätter förra resultatet">
            <i className="fas fa-sync"></i> Generera om
          </button>
          <button className="btn btn-link btn-sm" onClick={rec.dismiss}>
            <i className="fas fa-microphone"></i> Diktera igen
          </button>
        </div>
      )}

      {/* Live-transkript under pågående inspelning */}
      {isMine && rec.transcript && rec.phase !== 'done' && (
        <div className="recording-transcript">
          <label>Transkript (live):</label>
          <div className="recording-transcript-text">{rec.transcript}</div>
        </div>
      )}
    </div>
  );
};

export default RecordingPanel;
